import {
    IDocumentSimple,
    IReportTypeMetadata,
    ReportType,
    SparkContentReportType,
    ThreadReportType,
} from '@/interfaces/deltaGpt/IDocument.ts';

export const REPORT_TYPE_METADATA: Record<ReportType, IReportTypeMetadata> = {
    Pdf: {
        reportType: 'Pdf',
        label: 'Report',
        viewerKind: 'pdf',
        browseCategory: 'research',
        logo: { kind: 'image', source: '/images/homepage/pdf.png', alt: 'PDF' },
        fallbackThumbnail: '/thumbnails/fallback.png',
    },
    PowerBi: {
        reportType: 'PowerBi',
        label: 'Power BI',
        viewerKind: 'powerBi',
        browseCategory: 'research',
        logo: { kind: 'image', source: '/images/homepage/power-bi.png', alt: 'Power BI' },
        fallbackThumbnail: '/thumbnails/powerbi.png',
    },
    Excel: {
        reportType: 'Excel',
        label: 'Excel',
        viewerKind: 'excel',
        browseCategory: 'research',
        logo: { kind: 'image', source: '/images/homepage/excel.png', alt: 'Excel' },
        fallbackThumbnail: '/thumbnails/excel.png',
    },
    Podcast: {
        reportType: 'Podcast',
        label: 'Podcast',
        viewerKind: 'sparkContent',
        browseCategory: 'research',
        logo: {
            kind: 'icon', source: 'podcast', iconFamily: 'fas', alt: 'Podcast',
        },
        fallbackThumbnail: '/thumbnails/fallback.png',
        thumbnailObjectPosition: 'center center',
    },
    Webinar: {
        reportType: 'Webinar',
        label: 'Webinar',
        viewerKind: 'sparkContent',
        browseCategory: 'research',
        logo: {
            kind: 'icon', source: 'video', iconFamily: 'fas', alt: 'Webinar',
        },
        fallbackThumbnail: '/thumbnails/fallback.png',
    },
    Definition: {
        reportType: 'Definition',
        label: 'Definition',
        viewerKind: 'sparkContent',
        browseCategory: 'definition',
        fallbackThumbnail: '/thumbnails/fallback.png',
    },
    Feature: {
        reportType: 'Feature',
        label: 'Feature',
        viewerKind: 'sparkContent',
        browseCategory: 'thread',
        logo: {
            kind: 'icon', source: 'wand-magic-sparkles', iconFamily: 'fas', alt: 'Feature',
        },
        fallbackThumbnail: '/thumbnails/fallback.png',
    },
    Post: {
        reportType: 'Post',
        label: 'Post',
        viewerKind: 'sparkContent',
        browseCategory: 'thread',
        logo: {
            kind: 'icon', source: 'newspaper', iconFamily: 'fas', alt: 'Post',
        },
        fallbackThumbnail: '/thumbnails/fallback.png',
    },
    LinkedIn: {
        reportType: 'LinkedIn',
        label: 'LinkedIn',
        viewerKind: 'sparkContent',
        browseCategory: 'thread',
        logo: {
            kind: 'icon', source: 'linkedin', iconFamily: 'fab', alt: 'LinkedIn', colour: 'var(--lcpBlue)',
        },
        fallbackThumbnail: '/thumbnails/fallback.png',
    },
};

export const RESEARCH_LIST_REPORT_TYPES: Array<ReportType> = Object.values(REPORT_TYPE_METADATA)
    .filter(metadata => metadata.browseCategory === 'research')
    .map(metadata => metadata.reportType);
export const THREAD_REPORT_TYPES: Array<ThreadReportType> = Object.values(REPORT_TYPE_METADATA)
    .filter(metadata => metadata.browseCategory === 'thread')
    .map(metadata => metadata.reportType as ThreadReportType);
export const SPARK_CONTENT_REPORT_TYPES: Array<SparkContentReportType> = Object.values(REPORT_TYPE_METADATA)
    .filter(metadata => metadata.viewerKind === 'sparkContent')
    .map(metadata => metadata.reportType as SparkContentReportType);

export function getReportTypeMetadata (reportType: ReportType): IReportTypeMetadata {
    return REPORT_TYPE_METADATA[reportType];
}

export function isSparkContentReportType (reportType: ReportType): reportType is SparkContentReportType {
    return getReportTypeMetadata(reportType).viewerKind === 'sparkContent';
}

export function isResearchListReportType (reportType: ReportType): boolean {
    return getReportTypeMetadata(reportType).browseCategory === 'research';
}

export function isRichContentDocument (document: Pick<IDocumentSimple, 'reportType'>): boolean {
    return isThreadReportType(document.reportType);
}

export function isThreadReportType (reportType: ReportType): reportType is ThreadReportType {
    return getReportTypeMetadata(reportType).browseCategory === 'thread';
}

export function isLinkedInReportType (reportType: ReportType): boolean {
    return reportType === 'LinkedIn';
}

export function getReportTypeLabel (reportType: ReportType): string {
    return getReportTypeMetadata(reportType).label;
}

export function isDefinitionDocument (document: Pick<IDocumentSimple, 'reportType'>): boolean {
    return document.reportType === 'Definition';
}

export function isResearchBrowseDocument (document: Pick<IDocumentSimple, 'reportType'>): boolean {
    return isResearchListReportType(document.reportType);
}
